import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from "@angular/core";
import { Router } from "@angular/router";

import { Dialog } from "../../../core/providers/modal/modal-types";
import {
    LEAD_TIME_FILTER_OPTIONS,
    MOV_FILTER_OPTIONS,
} from "../../constants/pricelist.constants";
import {
    EXPORT_FORMAT_OPTIONS,
    ExportFormat,
} from "../../types/export-format.types";

export type CatalogExportScope = "full" | "filtered";
export type CatalogExportTab = "file" | "api";

export interface CatalogExportModalResult {
    scope: CatalogExportScope;
    format: ExportFormat;
    mov: string | null;
    shippingDate: string | null;
    includeRetailPrices: boolean;
}

export interface CatalogExportFilterTag {
    label: string;
    value: string;
}

const MAX_VISIBLE_FILTER_TAGS = 3;

@Component({
    selector: "vsf-catalog-export-modal",
    templateUrl: "./catalog-export-modal.component.html",
    styleUrls: ["./catalog-export-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogExportModalComponent
    implements Dialog<CatalogExportModalResult>
{
    resolveWith: (result?: CatalogExportModalResult) => void;

    format: ExportFormat = "xlsx";
    matchedCount = 0;
    filterTags: CatalogExportFilterTag[] = [];
    scope: CatalogExportScope = "full";
    activeTab: CatalogExportTab = "file";
    mov: string | null = null;
    shippingDate: string | null = null;
    includeRetailPrices = false;
    readonly formatOptions = EXPORT_FORMAT_OPTIONS;
    readonly movOptions = MOV_FILTER_OPTIONS.map((opt) => {
        const euros = Math.round(opt.max / 100);
        return { value: String(euros), label: `Up to ${euros} €` };
    });
    readonly shippingOptions = LEAD_TIME_FILTER_OPTIONS;
    readonly apiDocsPath = "/account/api-information";
    filtersExpanded = false;

    private _hasActiveFilters = false;

    constructor(
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    get hasActiveFilters(): boolean {
        return this._hasActiveFilters;
    }

    set hasActiveFilters(value: boolean) {
        this._hasActiveFilters = !!value;
        this.scope = this._hasActiveFilters ? "filtered" : "full";
    }

    get canUseFiltered(): boolean {
        return this.hasActiveFilters;
    }

    get visibleFilterTags(): CatalogExportFilterTag[] {
        if (this.filtersExpanded || this.filterTags.length <= MAX_VISIBLE_FILTER_TAGS) {
            return this.filterTags;
        }
        return this.filterTags.slice(0, MAX_VISIBLE_FILTER_TAGS);
    }

    get hiddenFilterTagCount(): number {
        if (this.filtersExpanded || this.filterTags.length <= MAX_VISIBLE_FILTER_TAGS) {
            return 0;
        }
        return this.filterTags.length - MAX_VISIBLE_FILTER_TAGS;
    }

    toggleFilterTags(): void {
        this.filtersExpanded = !this.filtersExpanded;
        this.changeDetectorRef.markForCheck();
    }

    selectTab(tab: CatalogExportTab): void {
        this.activeTab = tab;
        this.changeDetectorRef.markForCheck();
    }

    cancel(): void {
        this.resolveWith(undefined);
    }

    download(): void {
        const scope: CatalogExportScope =
            this.scope === "filtered" && this.canUseFiltered
                ? "filtered"
                : "full";
        this.resolveWith({
            scope,
            format: this.format,
            mov: this.mov,
            shippingDate: this.shippingDate,
            includeRetailPrices: this.includeRetailPrices,
        });
    }

    openDocumentation(): void {
        this.resolveWith(undefined);
        void this.router.navigateByUrl(this.apiDocsPath);
    }
}
